import autobind from "autobind-decorator";
import { observer } from "mobx-react";
import * as React from "react";
import Modal from "react-modal";
import styled from "styled-components";

import Style from "@client/utils/Style";

import headerBackgroundWide from "./images/header-background-wide.png";
import headerBackground from "./images/header-background.png";

const headerHeight = 48;

const getBackground = (wide?: boolean) =>
  `url("${
    wide ? headerBackgroundWide : headerBackground
  }") center/contain no-repeat`;

const Title = styled.div<{ wide?: boolean }>`
  height: ${headerHeight}px;
  font-size: 24px;
  font-weight: ${Style.fontWeights.light};
  text-align: center;
  line-height: 48px;
  color: white;
  background: ${props => getBackground(props.wide)};
`;

type Props = {
  children: React.ReactNode;
  isOpen: boolean;
  contentLabel: string;
  onRequestClose: () => void;
  contentStyle: React.CSSProperties;
  topBottomMargin?: number;
  title: string;
  componentRef?: (ref: any) => void;
  onScrollableRef?: () => HTMLElement;
  fixedHeight?: number;
};
@observer
class NewModal extends React.Component<Props> {
  static defaultProps: Partial<Props> = {
    topBottomMargin: 8,
  };

  scrollableRef: HTMLDivElement | null = null;
  windowScrollY = 0;
  modalDiv: ReactModal | null = null;

  UNSAFE_componentWillMount() {
    if (this.props.componentRef) {
      this.props.componentRef(this);
    }
  }

  componentDidMount() {
    window.addEventListener("resize", this.handleResize, true);
    setTimeout(() => {
      this.positionModal(0);
    }, 100);
  }

  UNSAFE_componentWillUpdate() {
    setTimeout(() => {
      this.positionModal(0);
    }, 100);
  }

  componentWillUnmount() {
    this.handleModalClose();
    window.removeEventListener("resize", this.handleResize, true);
  }

  @autobind
  handleResize() {
    this.positionModal(0);
  }

  handleChildUpdate(scrollableRef: HTMLDivElement, scrollOffset: number) {
    if (scrollableRef) {
      this.scrollableRef = scrollableRef;
    }

    setTimeout(() => {
      this.positionModal(scrollOffset);
    }, 100);
  }

  handleModalOpen() {
    if (!document.body) {
      return;
    }

    this.windowScrollY = window.scrollY;
    document.body.style.top = `${-this.windowScrollY}px`;
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.height = "100%";
  }

  handleModalClose() {
    if (!document.body) {
      return;
    }

    document.body.style.top = "";
    document.body.style.position = "";
    document.body.style.width = "";
    document.body.style.height = "";
    window.scroll(0, this.windowScrollY);
  }

  positionModal(scrollOffset: number) {
    // @ts-ignore
    const pageNode = this.modalDiv?.node.firstChild;
    const contentNode = pageNode && pageNode.firstChild;
    if (!contentNode || !pageNode) {
      return;
    }
    const topBottomMargin = this.props.topBottomMargin || 8;
    const scrollableRef =
      this.props.onScrollableRef && this.props.onScrollableRef();
    const scrollableNode = scrollableRef || this.scrollableRef || contentNode;
    const scrollTop = scrollableNode.scrollTop;
    contentNode.style.height = "auto";
    const contentDesiredHeight = this.props.fixedHeight
      ? this.props.fixedHeight + headerHeight
      : contentNode.offsetHeight;
    const left = pageNode.offsetWidth / 2 - contentNode.offsetWidth / 2;
    let top = pageNode.offsetHeight / 2 - contentDesiredHeight / 2;
    let height = this.props.fixedHeight
      ? `${this.props.fixedHeight + headerHeight}px`
      : "auto";

    if (top < topBottomMargin) {
      top = topBottomMargin;
      height = `${pageNode.offsetHeight - topBottomMargin * 2}px`;
    }

    contentNode.style.left = `${left}px`;
    contentNode.style.top = `${top}px`;
    contentNode.style.height = height;
    contentNode.style.opacity = 1;

    scrollableNode.scrollTop = scrollTop + scrollOffset;
  }

  renderTitle(contentWidth: React.CSSProperties["width"]) {
    if (!this.props.title) {
      return null;
    }

    return <Title wide={contentWidth === "800px"}>{this.props.title}</Title>;
  }

  render() {
    const modalStyle: {
      overlay: React.CSSProperties;
      content: React.CSSProperties;
    } = {
      overlay: {
        position: "fixed",
        zIndex: 1000,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(73, 76, 81, 0.69)",
      },
      content: {
        padding: 0,
        width: "576px",
        overflowX: "hidden",
        borderRadius: "4px",
        border: "none",
        bottom: "auto",
        top: "auto",
        left: "auto",
        right: "auto",
        transition: "all 0.2s",
        opacity: 0,
        display: "flex",
        flexDirection: "column",
      },
    };

    modalStyle.content = {
      ...modalStyle.content,
      ...this.props.contentStyle,
    };

    const children = React.Children.map(this.props.children, (child, i) =>
      i === 0
        ? child
        : React.cloneElement(child as any, {
            onResizeUpdate: (
              scrollableRef: HTMLDivElement,
              scrollOffset: number,
            ) => {
              this.handleChildUpdate(scrollableRef, scrollOffset);
            },
          }),
    );

    return (
      <Modal
        ref={m => {
          this.modalDiv = m;
        }}
        isOpen={this.props.isOpen}
        contentLabel={this.props.contentLabel || this.props.title}
        style={modalStyle}
        onRequestClose={this.props.onRequestClose}
        onAfterOpen={() => {
          this.handleModalOpen();
        }}
        ariaHideApp={false}
      >
        {this.renderTitle(modalStyle.content.width)}
        {children}
      </Modal>
    );
  }
}

export default NewModal;
